<template>
  <div class="tab-pane agents-tab-pane" :class="{ hidden: !active }">
    <!-- Skeleton: ainda esperando o primeiro carregamento -->
    <template v-if="scopeReady === false">
      <div class="agents-tab-grid">
        <div v-for="n in 12" :key="`skel-${n}`" class="agent-card ac-skel">
          <div class="ac-head">
            <div class="skel skel-av" style="width:58px;height:58px"></div>
            <div>
              <div class="skel skel-md" style="margin-bottom:6px"></div>
              <div class="skel skel-sm"></div>
            </div>
          </div>
          <div class="ac-body">
            <div class="ac-stat-row">
              <div class="ac-stat"><div class="skel skel-sm" style="margin:0 auto"></div></div>
              <div class="ac-stat"><div class="skel skel-sm" style="margin:0 auto"></div></div>
              <div class="ac-stat"><div class="skel skel-sm" style="margin:0 auto"></div></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else-if="!agents.length" class="empty" style="padding:40px">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="8" r="4"/>
        <path d="M20 21a8 8 0 1 0-16 0"/>
      </svg>
      <p>Nenhum agente encontrado</p>
      <small>Conversas sem responsável atribuído</small>
    </div>

    <template v-else>
    <!-- Barra de grupos — clicável, filtra os cards abaixo (clicar de novo no
         mesmo grupo limpa o filtro). "Atendendo" cruza com os 3 status de
         presença (é sobre ter conversa aberta, não sobre online/ocupado/offline). -->
    <div class="agents-group-bar">
      <button
        type="button"
        class="agb-item agb-online"
        :class="{ active: statusFilter === 'online' }"
        @click="toggleStatusFilter('online')"
      >
        <span class="agb-dot"></span>{{ groupCounts.online }} Online
      </button>
      <button
        type="button"
        class="agb-item agb-busy"
        :class="{ active: statusFilter === 'busy' }"
        @click="toggleStatusFilter('busy')"
      >
        <span class="agb-dot"></span>{{ groupCounts.busy }} Ocupado
      </button>
      <button
        type="button"
        class="agb-item agb-offline"
        :class="{ active: statusFilter === 'offline' }"
        @click="toggleStatusFilter('offline')"
      >
        <span class="agb-dot"></span>{{ groupCounts.offline }} Offline
      </button>
      <button
        type="button"
        class="agb-item agb-atendendo"
        :class="{ active: statusFilter === 'atendendo' }"
        @click="toggleStatusFilter('atendendo')"
      >
        <span class="agb-dot"></span>{{ groupCounts.atendendo }} Atendendo
      </button>
      <span class="agb-total">
        {{ filteredAgents.length }}<template v-if="statusFilter !== 'all'"> de {{ agents.length }}</template> agentes
      </span>
    </div>

    <div v-if="!filteredAgents.length" class="empty" style="padding:40px">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="8" r="4"/>
        <path d="M20 21a8 8 0 1 0-16 0"/>
      </svg>
      <p>Nenhum agente com esse status</p>
      <button type="button" class="ap-btn" @click="statusFilter = 'all'">Limpar filtro</button>
    </div>

    <div v-else class="agents-tab-grid">
      <div
        v-for="(agent, i) in pagedAgents"
        :key="agent.id"
        class="agent-card"
        :class="{ 'ac-offline': agent.availability === 'offline' }"
        :ref="el => setCardRef(el as HTMLElement, i)"
      >
        <button
          v-if="canUseAgentChatCard && agent.id !== myId"
          type="button"
          class="ac-chat-btn"
          :disabled="agent.availability !== 'online'"
          :title="agent.availability === 'online' ? `Conversar com ${agent.name}` : 'Agente offline'"
          @click.stop="openAgentChat(agent)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        </button>
        <div class="ac-head">
          <div
            class="ac-av-wrap"
            @mouseenter="onAvatarEnter(agent, $event)"
            @mouseleave="$emit('hidePopup')"
          >
            <img
              v-if="agent.thumbnail"
              :src="agent.thumbnail"
              :alt="agent.name"
              class="ac-av"
              style="width:58px;height:58px"
              @error="(e: Event) => handleImgError(e, agent)"
            />
            <div
              v-else
              class="ac-av"
              :style="{ background: avatarColor(agent.name) }"
            >{{ initials(agent.name) }}</div>
            <span class="ac-status-dot" :class="dotClass(agent.availability)"></span>
          </div>
          <div>
            <div class="ac-name">{{ agent.name }}</div>
            <div v-if="!agentTeamsMap || !Object.keys(agentTeamsMap).length" class="skel skel-sm"></div>
            <div v-else class="ac-role-lbl">{{ profileLabel(agent.id, agentTeamsMap?.[agent.id] ?? []) }}</div>
          </div>
          <span class="ac-avail-badge" :class="agent.availability">
            <span class="ac-bdot" :style="{ background: dotColor(agent.availability) }"></span>
            {{ availLabel(agent.availability) }}
          </span>
          <span v-if="agent.open > 0" class="ac-atend-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
            em atendimento
          </span>
        </div>
        <div class="ac-body">
          <div class="ac-stat-row">
            <div class="ac-stat">
              <div class="ac-stat-v" style="color:var(--blue)">{{ agent.open }}</div>
              <div class="ac-stat-l">Abertas</div>
            </div>
            <div class="ac-stat">
              <div class="ac-stat-v" style="color:var(--orange)">{{ agent.pending }}</div>
              <div class="ac-stat-l">Pendente</div>
            </div>
            <div class="ac-stat">
              <div class="ac-stat-v" style="color:var(--green)">{{ agent.resolved }}</div>
              <div class="ac-stat-l">Resolv.</div>
            </div>
          </div>
          <div class="ac-open-row" :class="agent.open > 0 ? 'has' : 'none'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {{ agent.open > 0 ? `${agent.open} conversa${agent.open > 1 ? 's' : ''} aberta${agent.open > 1 ? 's' : ''}` : 'Sem chats abertos' }}
          </div>
          <div
            v-if="agent.avgResolve > 0"
            class="ac-open-row"
            style="background:rgba(124,58,237,.07);color:#7C3AED;margin-bottom:8px"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px;flex-shrink:0">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            TMR: <strong style="margin-left:2px">{{ fmtDuration(agent.avgResolve) }}</strong>
            <span style="font-size:10px;opacity:.65;margin-left:4px">({{ agent.resolveSecs.length }} resolv.)</span>
          </div>
          <div v-else class="ac-open-row none" style="margin-bottom:8px">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px;flex-shrink:0">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            TMR: sem dados
          </div>
          <div class="ac-meta">
            <div v-if="agent.email" class="ac-meta-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <span :title="agent.email">{{ agent.email }}</span>
            </div>
            <div class="ac-meta-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span :title="[...agent.teams].join(', ') || '—'">{{ [...agent.teams].join(', ') || '—' }}</span>
            </div>
            <div class="ac-meta-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>{{ agent.lastAccess !== null ? fmtDateTimeSecs(agent.lastAccess) : 'Nunca acessou' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginação -->
    <div v-if="totalPages > 1" class="agents-pagination">
      <button
        class="ap-btn"
        :disabled="currentPage <= 1"
        @click="currentPage--"
        title="Página anterior"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <button
        v-for="p in totalPages"
        :key="p"
        class="ap-btn ap-page"
        :class="{ active: p === currentPage }"
        @click="currentPage = p"
      >{{ p }}</button>

      <button
        class="ap-btn"
        :disabled="currentPage >= totalPages"
        @click="currentPage++"
        title="Próxima página"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>

      <span class="ap-info">
        {{ (currentPage - 1) * PAGE_SIZE + 1 }}–{{ Math.min(currentPage * PAGE_SIZE, filteredAgents.length) }}
        de {{ filteredAgents.length }}
      </span>
    </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, nextTick, ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import type { Conversation, AgentStats, PopupData } from '@/types/dashboard'
import { avatarColor, initials, fmtDuration } from '@/composables/useHelpers'
import { profileLabel } from '@/composables/useAdmin'
import { useAdminDB } from '@/composables/useAdminDB'
import { fmtDateTimeSecs } from '@/utils/contactsFormat'
import { useAgentChat } from '@/composables/useAgentChat'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { usePermission } from '@/composables/usePermission'

const props = defineProps<{
  conversations: Conversation[]
  allAgents: { id: number; name: string; email: string; thumbnail: string; role: string; availability_status: string }[]
  agentTeamsMap?: Record<number, number[]>
  active: boolean
  // Fase 4 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md):
  // "Nenhum agente encontrado" aparecia por engano enquanto conversations/
  // allAgents ainda estavam vazios (indistinguível de "carregou e realmente
  // não tem agente") — `undefined`/`true` = mostra normal, só `false` explícito
  // (scopeReady ainda não virou true) mostra skeleton.
  scopeReady?: boolean
}>()

const emit = defineEmits<{
  showPopup: [data: PopupData, el: HTMLElement]
  hidePopup: []
}>()

const db = useAdminDB()
const lastAccessMap = ref<Record<number, number>>({})

// Chat entre agentes a partir do card — mesmo estado de módulo (singleton)
// usado pelo AgentChatDrawer.vue, só disparando openThread() daqui.
const store = useStore()
const myId  = computed<number>(() => store.getters.authUser?.id ?? 0)
const { panelOpen, openThread } = useAgentChat()
const { mode: railMode } = useChatRailMode()
const { can } = usePermission()
// 'agentChat.card' é o botão no card em si (perfis de acesso — Chat entre
// Agentes → "Chat pelo Card do Agente"); 'agentChat.view' também precisa
// estar liberado, senão o AgentChatDrawer.vue nem renderiza (seu próprio
// v-if usa essa mesma permissão) e o clique não abriria nada visível.
const canUseAgentChatCard = computed(() => can('agentChat.card') && can('agentChat.view'))

async function openAgentChat(agent: AgentStats) {
  if (agent.availability !== 'online' || agent.id === myId.value) return
  railMode.value = 'agent'
  panelOpen.value = true
  await openThread(myId.value, agent.id)
}

onMounted(async () => {
  try {
    const rows = await db.loadLastAccess()
    lastAccessMap.value = Object.fromEntries(rows.map(r => [r.user_id, r.last_access]))
  } catch {
    // sem dados de último acesso — cards mostram "Nunca acessou"
  }
})

const agents = computed<AgentStats[]>(() => {
  const map: Record<number, AgentStats> = {}

  // Semente com TODOS os agentes do Chatwoot (mesmo sem conversas)
  props.allAgents.forEach(a => {
    map[a.id] = {
      id:           a.id,
      name:         a.name,
      email:        a.email,
      thumbnail:    a.thumbnail,
      role:         a.role,
      availability: a.availability_status,
      total: 0, open: 0, pending: 0, resolved: 0,
      teams: new Set(),
      resolveSecs: [],
      avgResolve: 0,
      lastAccess: lastAccessMap.value[a.id] ?? null,
    } as AgentStats
  })

  // Acumula stats das conversas
  props.conversations.forEach(c => {
    if (!c.assignee) return
    const a = c.assignee
    if (!map[a.id]) {
      map[a.id] = { ...a, total: 0, open: 0, pending: 0, resolved: 0, teams: new Set(), resolveSecs: [], avgResolve: 0, lastAccess: lastAccessMap.value[a.id] ?? null }
    }
    map[a.id].total++
    if (c.status === 'open')     map[a.id].open++
    if (c.status === 'pending')  map[a.id].pending++
    if (c.status === 'resolved') {
      map[a.id].resolved++
      if (c.resolved_at && c.created_at && c.resolved_at > c.created_at) {
        map[a.id].resolveSecs.push(c.resolved_at - c.created_at)
      }
    }
    if (c.team?.name) map[a.id].teams.add(c.team.name)
    // Atualiza availability se a conversa trouxer dado mais recente
    if (a.availability) map[a.id].availability = a.availability
  })

  const order: Record<string, number> = { online: 0, busy: 1, offline: 2 }
  return Object.values(map)
    .map(a => ({
      ...a,
      avgResolve: a.resolveSecs.length
        ? Math.round(a.resolveSecs.reduce((s, v) => s + v, 0) / a.resolveSecs.length)
        : 0,
    }))
    .sort((a, b) => {
      const ao = order[a.availability] ?? 2, bo = order[b.availability] ?? 2
      return ao !== bo ? ao - bo : b.open - a.open
    })
})

const PAGE_SIZE = 12
const currentPage = ref(1)

// Filtro clicável da barra de grupos — 'atendendo' cruza com os 3 status de
// presença (online/busy/offline), então fica fora do Record<availability>.
type StatusFilter = 'all' | 'online' | 'busy' | 'offline' | 'atendendo'
const statusFilter = ref<StatusFilter>('all')
function toggleStatusFilter(status: Exclude<StatusFilter, 'all'>) {
  statusFilter.value = statusFilter.value === status ? 'all' : status
}

const filteredAgents = computed(() => {
  if (statusFilter.value === 'all') return agents.value
  if (statusFilter.value === 'atendendo') return agents.value.filter(a => a.open > 0)
  return agents.value.filter(a => a.availability === statusFilter.value)
})

const totalPages = computed(() => Math.ceil(filteredAgents.value.length / PAGE_SIZE))

const pagedAgents = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE
  return filteredAgents.value.slice(start, start + PAGE_SIZE)
})

const groupCounts = computed(() => {
  let online = 0, busy = 0, offline = 0, atendendo = 0
  agents.value.forEach(a => {
    if (a.availability === 'online') online++
    else if (a.availability === 'busy') busy++
    else offline++
    if (a.open > 0) atendendo++
  })
  return { online, busy, offline, atendendo }
})

watch([agents, statusFilter], () => { currentPage.value = 1 })

const cardRefs: HTMLElement[] = []
function setCardRef(el: HTMLElement | null, i: number) {
  if (el) cardRefs[i] = el
}

// scopeReady entra nas dependências porque a troca do skeleton pro grid real
// (v-if="scopeReady === false" → v-else) pode acontecer DEPOIS de pagedAgents
// já ter mudado de valor — sem isso, o watch dispara com cardRefs ainda vazio
// (template no branch do skeleton) e os cards ficam presos em opacity:0 até
// o usuário trocar de aba e voltar (o que alterna `active` e força o retrigger).
watch([pagedAgents, () => props.active, () => props.scopeReady], async ([, isActive, isScopeReady]) => {
  if (!isActive || isScopeReady === false) return
  await nextTick()
  cardRefs.forEach((card, i) => setTimeout(() => card?.classList.add('in'), i * 45))
})

function dotClass(av: string) {
  return { online: 'acd-online', offline: 'acd-offline', busy: 'acd-busy' }[av] || 'acd-offline'
}
function dotColor(av: string) {
  return { online: 'var(--green)', offline: 'var(--ink-40)', busy: 'var(--orange)' }[av] || 'var(--ink-40)'
}
function availLabel(av: string) {
  return { online: 'Online', offline: 'Offline', busy: 'Ocupado' }[av] || 'Offline'
}
function handleImgError(e: Event, agent: AgentStats) {
  const img = e.target as HTMLImageElement
  const div = document.createElement('div')
  div.className = 'ac-av'
  div.style.background = avatarColor(agent.name)
  div.textContent = initials(agent.name)
  img.replaceWith(div)
}
function onAvatarEnter(agent: AgentStats, e: MouseEvent) {
  emit('showPopup', {
    name:      agent.name,
    role:      profileLabel(agent.id, props.agentTeamsMap?.[agent.id] ?? []),
    email:     agent.email || '',
    thumbnail: agent.thumbnail || '',
    status:    agent.availability || 'offline',
    convs:     agent.total,
    openConvs: agent.open,
    team:      [...agent.teams].join(', ') || null,
  }, e.currentTarget as HTMLElement)
}
</script>
